'use client'

import { type MascotMood } from '@/lib/bug-mascot-moods'

/**
 * BugMascot — an inline SVG caterpillar with DRAMATIC, UNMISSABLE animations.
 *
 * Design principles:
 * - Animations must be OBVIOUS at 58px — big movements, big elements
 * - Each mood must be INSTANTLY distinguishable from the others
 * - Extra elements (tears, food, ball, zzz) are LARGE
 * - Body movements are EXAGGERATED (15°+ rotations, 10px+ translations)
 *
 * All animations: CSS transforms + opacity (GPU-accelerated).
 */

interface BugMascotProps {
  mood: MascotMood
  reducedMotion?: boolean
  size?: number
}

export default function BugMascot({ mood, reducedMotion = false, size = 56 }: BugMascotProps) {
  const moodClass = reducedMotion ? 'bm-idle' : `bm-${mood}`

  return (
    <>
      <svg
        className={`bug-mascot ${moodClass}`}
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ display: 'block', overflow: 'visible' }}
      >
        {/* ── Shadow ──────────────────────────────── */}
        <ellipse cx="50" cy="92" rx="22" ry="5" fill="rgba(0,0,0,0.22)" className="bm-shadow" />

        {/* ── Body group ── */}
        <g className="bm-body-group">
          {/* Rear segment */}
          <ellipse cx="50" cy="68" rx="18" ry="14" fill="#4ade80" />
          <path d="M35 63 Q50 60 65 63" stroke="#22c55e" strokeWidth="1" opacity="0.5" fill="none" />
          <path d="M34 68 Q50 65 66 68" stroke="#22c55e" strokeWidth="1" opacity="0.5" fill="none" />

          {/* Mid segment */}
          <ellipse cx="50" cy="55" rx="20" ry="16" fill="#22c55e" />
          <ellipse cx="50" cy="57" rx="12" ry="9" fill="#86efac" opacity="0.35" />

          {/* Head */}
          <circle cx="50" cy="36" r="18" fill="#16a34a" />
          <ellipse cx="46" cy="30" rx="8" ry="5" fill="#22c55e" opacity="0.3" />

          {/* ── Cheeks ──────────────────────────── */}
          <circle cx="36" cy="42" r="5" fill="#fb923c" opacity="0.25" className="bm-cheek-l" />
          <circle cx="64" cy="42" r="5" fill="#fb923c" opacity="0.25" className="bm-cheek-r" />

          {/* ── Legs ────────────────────────────── */}
          <g className="bm-legs" opacity="0.6">
            <ellipse cx="33" cy="64" rx="5" ry="2.5" fill="#15803d" className="bm-leg-l1" />
            <ellipse cx="67" cy="64" rx="5" ry="2.5" fill="#15803d" className="bm-leg-r1" />
            <ellipse cx="32" cy="55" rx="4" ry="2" fill="#15803d" className="bm-leg-l2" />
            <ellipse cx="68" cy="55" rx="4" ry="2" fill="#15803d" className="bm-leg-r2" />
          </g>

          {/* ── Antennae ────────────────────────── */}
          <g className="bm-antennae">
            <path d="M42 22 Q36 8 28 4" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" fill="none" className="bm-ant-l" />
            <circle cx="28" cy="4" r="4" fill="#4ade80" className="bm-ant-tip-l" />
            <path d="M58 22 Q64 8 72 4" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" fill="none" className="bm-ant-r" />
            <circle cx="72" cy="4" r="4" fill="#4ade80" className="bm-ant-tip-r" />
          </g>

          {/* ── Eyes ─────────────────────────────── */}
          <g className="bm-eyes">
            <ellipse cx="42" cy="34" rx="6.5" ry="7" fill="white" className="bm-eye-l" />
            <g className="bm-pupil-group-l">
              <circle cx="43.5" cy="34" r="3.2" fill="#1a1a2e" />
              <circle cx="45" cy="32.5" r="1.3" fill="white" opacity="0.9" />
            </g>
            <ellipse cx="58" cy="34" rx="6.5" ry="7" fill="white" className="bm-eye-r" />
            <g className="bm-pupil-group-r">
              <circle cx="59.5" cy="34" r="3.2" fill="#1a1a2e" />
              <circle cx="61" cy="32.5" r="1.3" fill="white" opacity="0.9" />
            </g>
            {/* Eyelids */}
            <ellipse cx="42" cy="34" rx="7" ry="0" fill="#16a34a" className="bm-lid-l" />
            <ellipse cx="58" cy="34" rx="7" ry="0" fill="#16a34a" className="bm-lid-r" />
          </g>

          {/* ── Mouth ────────────────────────────── */}
          <g className="bm-mouth-group">
            {/* Default smile */}
            <path d="M44 44 Q50 49 56 44" stroke="#15803d" strokeWidth="1.8" strokeLinecap="round" fill="none" className="bm-mouth" />
            {/* BIG open mouth for laugh */}
            <ellipse cx="50" cy="47" rx="8" ry="6" fill="#15803d" opacity="0" className="bm-mouth-open" />
            <ellipse cx="50" cy="45" rx="6" ry="3" fill="#dc2626" opacity="0" className="bm-tongue" />
            {/* Frown for cry */}
            <path d="M44 48 Q50 43 56 48" stroke="#15803d" strokeWidth="1.8" strokeLinecap="round" fill="none" opacity="0" className="bm-frown" />
            {/* Snore mouth — small O */}
            <ellipse cx="50" cy="46" rx="4" ry="5" fill="#15803d" opacity="0" className="bm-mouth-snore" />
            {/* Eating mouth — chomping */}
            <ellipse cx="50" cy="46" rx="6" ry="4" fill="#15803d" opacity="0" className="bm-mouth-chomp" />
          </g>

          {/* ── Extras ───────────────────────────── */}
          <g className="bm-extras">
            {/* Sneeze particles */}
            <circle cx="70" cy="38" r="3" fill="#fbbf24" opacity="0" className="bm-particle-1" />
            <circle cx="74" cy="32" r="2.5" fill="#fb923c" opacity="0" className="bm-particle-2" />
            <circle cx="72" cy="44" r="2.8" fill="#fbbf24" opacity="0" className="bm-particle-3" />

            {/* BIG tears */}
            <ellipse cx="34" cy="44" rx="3.5" ry="5" fill="#60a5fa" opacity="0" className="bm-tear-l" />
            <ellipse cx="66" cy="44" rx="3.5" ry="5" fill="#60a5fa" opacity="0" className="bm-tear-r" />
            <ellipse cx="35" cy="46" rx="2.5" ry="4" fill="#93c5fd" opacity="0" className="bm-tear-l2" />
            <ellipse cx="65" cy="46" rx="2.5" ry="4" fill="#93c5fd" opacity="0" className="bm-tear-r2" />

            {/* BIG Zzz for snoring */}
            <text x="64" y="16" fill="#a78bfa" fontSize="18" fontWeight="bold" opacity="0" className="bm-zzz-1">Z</text>
            <text x="76" y="6" fill="#c4b5fd" fontSize="14" fontWeight="bold" opacity="0" className="bm-zzz-2">z</text>
            <text x="82" y="-2" fill="#ddd6fe" fontSize="10" fontWeight="bold" opacity="0" className="bm-zzz-3">z</text>

            {/* Thought dots */}
            <circle cx="72" cy="22" r="3.5" fill="#94a3b8" opacity="0" className="bm-dot-1" />
            <circle cx="80" cy="14" r="2.5" fill="#94a3b8" opacity="0" className="bm-dot-2" />
            <circle cx="86" cy="8" r="2" fill="#94a3b8" opacity="0" className="bm-dot-3" />

            {/* Sparkles */}
            <text x="18" y="22" fontSize="14" opacity="0" className="bm-sparkle-1">&#10022;</text>
            <text x="72" y="14" fontSize="12" opacity="0" className="bm-sparkle-2">&#10022;</text>
            <text x="14" y="52" fontSize="11" opacity="0" className="bm-sparkle-3">&#10022;</text>
            <text x="78" y="56" fontSize="13" opacity="0" className="bm-sparkle-4">&#10022;</text>

            {/* Sweat drop */}
            <ellipse cx="68" cy="26" rx="2.5" ry="3.5" fill="#60a5fa" opacity="0" className="bm-sweat" />

            {/* Food item (leaf) for eating */}
            <g className="bm-food" opacity="0">
              <ellipse cx="70" cy="42" rx="6" ry="4" fill="#4ade80" />
              <path d="M70 38 L70 42" stroke="#16a34a" strokeWidth="1.5" />
              <path d="M66 41 Q70 39 74 41" stroke="#16a34a" strokeWidth="0.8" fill="none" />
            </g>
            {/* Food crumbs */}
            <circle cx="62" cy="50" r="2" fill="#86efac" opacity="0" className="bm-crumb-1" />
            <circle cx="58" cy="52" r="1.5" fill="#4ade80" opacity="0" className="bm-crumb-2" />
            <circle cx="65" cy="53" r="1.2" fill="#86efac" opacity="0" className="bm-crumb-3" />

            {/* Ball for kicking */}
            <g className="bm-ball" opacity="0">
              <circle cx="28" cy="76" r="7" fill="#f97316" />
              <circle cx="28" cy="76" r="7" fill="none" stroke="#ea580c" strokeWidth="1" />
              <path d="M24 72 L32 80 M32 72 L24 80" stroke="#ea580c" strokeWidth="0.8" opacity="0.5" />
            </g>

            {/* Drool */}
            <ellipse cx="54" cy="50" rx="2.5" ry="4" fill="#60a5fa" opacity="0" className="bm-drool" />
          </g>
        </g>
      </svg>

      <style>{`
        /* ═══ BASE ═══════════════════════════════════════════════ */
        .bug-mascot { overflow: visible; }
        .bug-mascot * { transform-origin: center; }
        .bm-body-group { transform-origin: 50px 50px; }
        .bm-antennae { transform-origin: 50px 22px; }
        .bm-eyes { transform-origin: 50px 34px; }
        .bm-mouth-group { transform-origin: 50px 46px; }
        .bm-shadow { transform-origin: 50px 92px; }
        .bm-legs { transform-origin: 50px 60px; }

        /* ═══ IDLE — gentle breathing + antenna sway ════════════ */
        .bm-idle .bm-body-group { animation: bm-breathe 2.8s ease-in-out infinite; }
        .bm-idle .bm-shadow { animation: bm-shadow-breathe 2.8s ease-in-out infinite; }
        .bm-idle .bm-antennae { animation: bm-idle-ant 3.5s ease-in-out infinite; }
        @keyframes bm-breathe {
          0%, 100% { transform: translateY(0) scaleY(1); }
          50%      { transform: translateY(-4px) scaleY(1.04); }
        }
        @keyframes bm-shadow-breathe {
          0%, 100% { transform: scaleX(1); opacity: 0.22; }
          50%      { transform: scaleX(0.85); opacity: 0.12; }
        }
        @keyframes bm-idle-ant {
          0%, 100% { transform: rotate(0deg); }
          30%      { transform: rotate(3deg); }
          70%      { transform: rotate(-3deg); }
        }

        /* ═══ BLINK ═════════════════════════════════════════════ */
        .bm-blink .bm-body-group { animation: bm-breathe 2.8s ease-in-out infinite; }
        .bm-blink .bm-lid-l, .bm-blink .bm-lid-r { animation: bm-blink-lid 0.5s ease-in-out forwards; }
        @keyframes bm-blink-lid {
          0%   { transform: scaleY(0); opacity: 0; }
          20%  { transform: scaleY(14); opacity: 1; }
          50%  { transform: scaleY(14); opacity: 1; }
          75%  { transform: scaleY(0); opacity: 0; }
        }

        /* ═══ CURIOUS — big tilt + look around ══════════════════ */
        .bm-curious .bm-body-group { animation: bm-curious-tilt 2s ease-in-out forwards; }
        @keyframes bm-curious-tilt {
          0%   { transform: rotate(0deg); }
          20%  { transform: rotate(15deg) translateY(-4px); }
          60%  { transform: rotate(-8deg) translateY(-2px); }
          100% { transform: rotate(0deg); }
        }
        .bm-curious .bm-pupil-group-l, .bm-curious .bm-pupil-group-r { animation: bm-look-around 2s ease-in-out; }
        @keyframes bm-look-around {
          0%   { transform: translateX(0); }
          20%  { transform: translateX(4px) translateY(-2px); }
          50%  { transform: translateX(-3px) translateY(2px); }
          100% { transform: translateX(0); }
        }
        .bm-curious .bm-ant-tip-l { animation: bm-ant-perk-l 1.4s ease-in-out; }
        .bm-curious .bm-ant-tip-r { animation: bm-ant-perk-r 1.4s ease-in-out 0.2s; }
        @keyframes bm-ant-perk-l {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40%      { transform: translate(-5px, -8px) scale(1.4); }
        }
        @keyframes bm-ant-perk-r {
          0%, 100% { transform: translate(0, 0) scale(1); }
          40%      { transform: translate(5px, -8px) scale(1.4); }
        }

        /* ═══ SLEEPY / SNORING — sway, open mouth, BIG Zzz ═════ */
        .bm-sleepy .bm-body-group { animation: bm-sleepy-sway 2.5s ease-in-out infinite; }
        @keyframes bm-sleepy-sway {
          0%, 100% { transform: translateY(3px) rotate(0deg); }
          50%      { transform: translateY(5px) rotate(-8deg); }
        }
        .bm-sleepy .bm-lid-l, .bm-sleepy .bm-lid-r { animation: bm-droop-lids 0.6s ease-out forwards; }
        @keyframes bm-droop-lids {
          0%   { transform: scaleY(0); opacity: 0; }
          100% { transform: scaleY(10); opacity: 0.85; }
        }
        .bm-sleepy .bm-mouth { animation: bm-hide 0.3s forwards; }
        .bm-sleepy .bm-mouth-snore { animation: bm-snore-mouth 1.2s ease-in-out infinite 0.3s; }
        @keyframes bm-snore-mouth {
          0%   { opacity: 0.9; transform: scaleY(1) scaleX(1); }
          50%  { opacity: 0.9; transform: scaleY(1.5) scaleX(1.3); }
          100% { opacity: 0.9; transform: scaleY(1) scaleX(1); }
        }
        .bm-sleepy .bm-zzz-1 { animation: bm-zzz-float 3s ease-out 0.2s forwards; }
        .bm-sleepy .bm-zzz-2 { animation: bm-zzz-float 3s ease-out 0.8s forwards; }
        .bm-sleepy .bm-zzz-3 { animation: bm-zzz-float 3s ease-out 1.4s forwards; }
        @keyframes bm-zzz-float {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.5); }
          10%  { opacity: 1; transform: translate(2px, -4px) scale(1); }
          40%  { opacity: 0.8; transform: translate(8px, -16px) scale(1.1); }
          70%  { opacity: 0.5; transform: translate(14px, -28px) scale(0.9); }
          100% { opacity: 0; transform: translate(20px, -38px) scale(0.6); }
        }

        /* ═══ SNEEZE — wind-up, snap, particles ═════════════════ */
        .bm-sneeze .bm-body-group { animation: bm-sneeze-body 1.6s ease-in-out forwards; }
        @keyframes bm-sneeze-body {
          0%   { transform: translateY(0) scaleY(1) rotate(0); }
          15%  { transform: translateY(-6px) scaleY(1.1) rotate(-2deg); }
          30%  { transform: translateY(-10px) scaleY(1.15) rotate(-3deg); }
          42%  { transform: translateY(12px) scaleY(0.8) rotate(8deg); }
          55%  { transform: translateY(6px) scaleY(0.9) rotate(4deg); }
          70%  { transform: translateY(-2px) scaleY(1.02) rotate(0); }
          100% { transform: translateY(0) scaleY(1) rotate(0); }
        }
        .bm-sneeze .bm-lid-l, .bm-sneeze .bm-lid-r { animation: bm-sneeze-eyes 1.6s ease-in-out forwards; }
        @keyframes bm-sneeze-eyes {
          0%   { transform: scaleY(0); opacity: 0; }
          35%  { transform: scaleY(0); opacity: 0; }
          42%  { transform: scaleY(14); opacity: 1; }
          65%  { transform: scaleY(14); opacity: 1; }
          80%  { transform: scaleY(0); opacity: 0; }
        }
        .bm-sneeze .bm-particle-1 { animation: bm-sneeze-p1 0.9s ease-out 0.4s forwards; }
        .bm-sneeze .bm-particle-2 { animation: bm-sneeze-p2 0.8s ease-out 0.42s forwards; }
        .bm-sneeze .bm-particle-3 { animation: bm-sneeze-p3 0.85s ease-out 0.45s forwards; }
        @keyframes bm-sneeze-p1 { 0% { opacity: 0; transform: translate(0,0) scale(1); } 10% { opacity: 1; } 100% { opacity: 0; transform: translate(30px, -8px) scale(0.3); } }
        @keyframes bm-sneeze-p2 { 0% { opacity: 0; transform: translate(0,0) scale(1); } 10% { opacity: 1; } 100% { opacity: 0; transform: translate(32px, -16px) scale(0.2); } }
        @keyframes bm-sneeze-p3 { 0% { opacity: 0; transform: translate(0,0) scale(1); } 10% { opacity: 1; } 100% { opacity: 0; transform: translate(26px, 6px) scale(0.3); } }
        .bm-sneeze .bm-cheek-l, .bm-sneeze .bm-cheek-r { animation: bm-blush 0.3s ease-out 0.35s forwards; }

        /* ═══ LAUGH — HUGE mouth, hard bouncing, long ═══════════ */
        .bm-laugh .bm-body-group { animation: bm-laugh-bounce 3s ease-in-out; }
        @keyframes bm-laugh-bounce {
          0%   { transform: translateY(0) scaleX(1) rotate(0); }
          8%   { transform: translateY(-12px) scaleX(1.1) rotate(4deg); }
          16%  { transform: translateY(4px) scaleX(0.92) rotate(-3deg); }
          24%  { transform: translateY(-10px) scaleX(1.08) rotate(3deg); }
          32%  { transform: translateY(3px) scaleX(0.94) rotate(-2deg); }
          40%  { transform: translateY(-8px) scaleX(1.06) rotate(2deg); }
          48%  { transform: translateY(2px) scaleX(0.96) rotate(-1deg); }
          56%  { transform: translateY(-5px) scaleX(1.04) rotate(1deg); }
          65%  { transform: translateY(1px) scaleX(0.98); }
          75%  { transform: translateY(-2px) scaleX(1.01); }
          100% { transform: translateY(0) scaleX(1) rotate(0); }
        }
        .bm-laugh .bm-lid-l, .bm-laugh .bm-lid-r { animation: bm-laugh-squint 3s ease-in-out; }
        @keyframes bm-laugh-squint {
          0%   { transform: scaleY(0); opacity: 0; }
          8%   { transform: scaleY(9); opacity: 0.8; }
          80%  { transform: scaleY(9); opacity: 0.8; }
          92%  { transform: scaleY(0); opacity: 0; }
        }
        .bm-laugh .bm-cheek-l, .bm-laugh .bm-cheek-r { animation: bm-blush 0.3s ease-out forwards; }
        .bm-laugh .bm-mouth { animation: bm-hide 0.2s forwards; }
        .bm-laugh .bm-mouth-open { animation: bm-laugh-big-mouth 3s ease-in-out; }
        .bm-laugh .bm-tongue { animation: bm-laugh-tongue 3s ease-in-out; }
        @keyframes bm-laugh-big-mouth {
          0%   { opacity: 0; transform: scale(0.5); }
          6%   { opacity: 1; transform: scale(1.2); }
          15%  { opacity: 1; transform: scale(1.5); }
          25%  { opacity: 1; transform: scale(1.1); }
          35%  { opacity: 1; transform: scale(1.5); }
          45%  { opacity: 1; transform: scale(1.1); }
          55%  { opacity: 1; transform: scale(1.4); }
          75%  { opacity: 1; transform: scale(1.2); }
          88%  { opacity: 1; transform: scale(1); }
          95%  { opacity: 0; transform: scale(0.5); }
        }
        @keyframes bm-laugh-tongue {
          0% { opacity: 0; } 6% { opacity: 0.8; transform: translateY(2px); } 80% { opacity: 0.8; transform: translateY(2px); } 92% { opacity: 0; }
        }

        /* ═══ CRY — BIG streaming tears, frown, shaking ════════ */
        .bm-cry .bm-body-group { animation: bm-cry-shake 0.35s ease-in-out infinite; }
        @keyframes bm-cry-shake {
          0%, 100% { transform: translateX(0) translateY(0); }
          25%      { transform: translateX(-4px) translateY(1px); }
          75%      { transform: translateX(4px) translateY(1px); }
        }
        .bm-cry .bm-tear-l  { animation: bm-tear-fall-l 0.8s ease-in infinite 0.1s; }
        .bm-cry .bm-tear-r  { animation: bm-tear-fall-r 0.8s ease-in infinite 0.4s; }
        .bm-cry .bm-tear-l2 { animation: bm-tear-fall-l 0.9s ease-in infinite 0.5s; }
        .bm-cry .bm-tear-r2 { animation: bm-tear-fall-r 0.9s ease-in infinite 0.8s; }
        @keyframes bm-tear-fall-l {
          0%   { opacity: 0; transform: translate(0, 0) scaleY(1); }
          5%   { opacity: 0.9; }
          100% { opacity: 0; transform: translate(-8px, 35px) scaleY(2); }
        }
        @keyframes bm-tear-fall-r {
          0%   { opacity: 0; transform: translate(0, 0) scaleY(1); }
          5%   { opacity: 0.9; }
          100% { opacity: 0; transform: translate(8px, 35px) scaleY(2); }
        }
        .bm-cry .bm-lid-l, .bm-cry .bm-lid-r { animation: bm-cry-squint 0.5s ease-in-out infinite alternate; }
        @keyframes bm-cry-squint {
          0%   { transform: scaleY(5); opacity: 0.6; }
          100% { transform: scaleY(8); opacity: 0.8; }
        }
        .bm-cry .bm-mouth { animation: bm-hide 0.2s forwards; }
        .bm-cry .bm-frown { animation: bm-cry-frown 0.3s ease-in-out infinite alternate; }
        @keyframes bm-cry-frown {
          0%   { opacity: 1; transform: scaleX(0.8) translateY(1px); }
          100% { opacity: 1; transform: scaleX(0.9) translateY(2px); }
        }
        .bm-cry .bm-antennae { animation: bm-cry-droop 0.6s ease-out forwards; }
        @keyframes bm-cry-droop {
          0%   { transform: translateY(0) rotate(0); }
          100% { transform: translateY(6px) rotate(5deg) scaleY(0.85); }
        }

        /* ═══ HUNGRY / EATING — food, chomps, crumbs ════════════ */
        .bm-hungry .bm-body-group { animation: bm-eat-chomp 2.6s ease-in-out; }
        @keyframes bm-eat-chomp {
          0%   { transform: translateX(0) rotate(0); }
          15%  { transform: translateX(4px) rotate(3deg); }
          25%  { transform: translateX(6px) rotate(5deg); }
          30%  { transform: translateX(4px) rotate(2deg); }
          35%  { transform: translateX(6px) rotate(5deg); }
          40%  { transform: translateX(4px) rotate(2deg); }
          45%  { transform: translateX(6px) rotate(4deg); }
          55%  { transform: translateX(0) rotate(0); }
        }
        .bm-hungry .bm-food { animation: bm-food-appear 2.6s ease-in-out forwards; }
        @keyframes bm-food-appear {
          0%   { opacity: 0; transform: translate(10px, 0) scale(1.2); }
          10%  { opacity: 1; transform: translate(4px, 0) scale(1); }
          25%  { opacity: 1; transform: translate(0, 0) scale(0.9); }
          45%  { opacity: 0.8; transform: translate(-4px, 2px) scale(0.4); }
          55%  { opacity: 0; transform: translate(-6px, 4px) scale(0.1); }
        }
        .bm-hungry .bm-mouth { animation: bm-hide 0.2s forwards; }
        .bm-hungry .bm-mouth-chomp { animation: bm-chomp 0.4s ease-in-out 5 0.15s; }
        @keyframes bm-chomp {
          0%   { opacity: 1; transform: scaleY(1) scaleX(1); }
          50%  { opacity: 1; transform: scaleY(0.3) scaleX(1.2); }
          100% { opacity: 1; transform: scaleY(1) scaleX(1); }
        }
        .bm-hungry .bm-pupil-group-l, .bm-hungry .bm-pupil-group-r { animation: bm-hungry-look 2.6s ease-in-out; }
        @keyframes bm-hungry-look {
          0% { transform: translate(0, 0); } 10% { transform: translate(3px, 2px); } 50% { transform: translate(3px, 2px); } 60% { transform: translate(0, 0); }
        }
        .bm-hungry .bm-cheek-l, .bm-hungry .bm-cheek-r { animation: bm-blush 0.5s ease-out 0.2s forwards; }
        .bm-hungry .bm-crumb-1 { animation: bm-crumb 0.6s ease-out 1s forwards; }
        .bm-hungry .bm-crumb-2 { animation: bm-crumb 0.5s ease-out 1.2s forwards; }
        .bm-hungry .bm-crumb-3 { animation: bm-crumb 0.55s ease-out 1.4s forwards; }
        @keyframes bm-crumb { 0% { opacity: 0; transform: translate(0, 0) scale(1); } 15% { opacity: 0.8; } 100% { opacity: 0; transform: translate(-8px, 16px) scale(0.4); } }

        /* ═══ THINKING — eyes up, dots, tilt ════════════════════ */
        .bm-thinking .bm-body-group { animation: bm-think-tilt 2.4s ease-in-out; }
        @keyframes bm-think-tilt {
          0% { transform: rotate(0); } 15% { transform: rotate(-6deg) translateY(-2px); } 80% { transform: rotate(-6deg) translateY(-2px); } 100% { transform: rotate(0); }
        }
        .bm-thinking .bm-pupil-group-l, .bm-thinking .bm-pupil-group-r { animation: bm-think-look 2.4s ease-in-out; }
        @keyframes bm-think-look {
          0% { transform: translate(0, 0); } 15% { transform: translate(-2px, -4px); } 80% { transform: translate(-2px, -4px); } 100% { transform: translate(0, 0); }
        }
        .bm-thinking .bm-dot-1 { animation: bm-think-dot 2s ease-out 0.2s forwards; }
        .bm-thinking .bm-dot-2 { animation: bm-think-dot 2s ease-out 0.5s forwards; }
        .bm-thinking .bm-dot-3 { animation: bm-think-dot 2s ease-out 0.8s forwards; }
        @keyframes bm-think-dot {
          0% { opacity: 0; transform: scale(0); } 15% { opacity: 0.8; transform: scale(1.3); } 25% { opacity: 0.7; transform: scale(1); } 70% { opacity: 0.7; transform: scale(1); } 100% { opacity: 0; transform: scale(0.3); }
        }

        /* ═══ GROW-POP ══════════════════════════════════════════ */
        .bm-growPop .bm-body-group { animation: bm-grow-pop 2.2s ease-in-out forwards; }
        @keyframes bm-grow-pop {
          0% { transform: scale(1); } 25% { transform: scale(1.35); } 40% { transform: scale(1.45); }
          48% { transform: scale(0.15); opacity: 0.3; } 55% { transform: scale(0.15); opacity: 0; }
          68% { transform: scale(0.5); opacity: 0.5; } 80% { transform: scale(1.1); opacity: 1; }
          90% { transform: scale(0.95); opacity: 1; } 100% { transform: scale(1); opacity: 1; }
        }
        .bm-growPop .bm-sparkle-1 { animation: bm-sparkle-burst 0.8s ease-out 1s forwards; }
        .bm-growPop .bm-sparkle-2 { animation: bm-sparkle-burst 0.7s ease-out 1.1s forwards; }
        .bm-growPop .bm-sparkle-3 { animation: bm-sparkle-burst 0.6s ease-out 1.05s forwards; }
        .bm-growPop .bm-sparkle-4 { animation: bm-sparkle-burst 0.7s ease-out 1.15s forwards; }
        .bm-growPop .bm-shadow { animation: bm-grow-shadow 2.2s ease-in-out forwards; }
        @keyframes bm-grow-shadow {
          0% { transform: scaleX(1); } 25% { transform: scaleX(1.4); } 48% { transform: scaleX(0.2); opacity: 0.03; }
          80% { transform: scaleX(1.1); opacity: 0.22; } 100% { transform: scaleX(1); opacity: 0.22; }
        }

        /* ═══ EXCITED (hover) ═══════════════════════════════════ */
        .bm-excited .bm-body-group { animation: bm-excited-bounce 0.6s ease-out; }
        @keyframes bm-excited-bounce {
          0% { transform: translateY(0) scale(1); } 25% { transform: translateY(-14px) scale(1.1); }
          45% { transform: translateY(4px) scale(0.92); } 65% { transform: translateY(-4px) scale(1.04); }
          85% { transform: translateY(1px) scale(0.99); } 100% { transform: translateY(0) scale(1); }
        }
        .bm-excited .bm-antennae { animation: bm-wiggle-ant 0.8s ease-in-out; }
        @keyframes bm-wiggle-ant {
          0% { transform: rotate(0); } 15% { transform: rotate(15deg); } 40% { transform: rotate(-15deg); }
          65% { transform: rotate(8deg); } 85% { transform: rotate(-3deg); } 100% { transform: rotate(0); }
        }
        .bm-excited .bm-cheek-l, .bm-excited .bm-cheek-r { animation: bm-blush 0.3s ease-out forwards; }
        .bm-excited .bm-eye-l, .bm-excited .bm-eye-r { animation: bm-eyes-widen 0.25s ease-out forwards; }
        @keyframes bm-eyes-widen { 0% { transform: scaleY(1); } 100% { transform: scaleY(1.2); } }
        .bm-excited .bm-shadow { animation: bm-shadow-bounce 0.6s ease-out; }
        @keyframes bm-shadow-bounce {
          0% { transform: scaleX(1); } 25% { transform: scaleX(0.5); } 45% { transform: scaleX(1.15); } 100% { transform: scaleX(1); }
        }

        /* ═══ CELEBRATE (success) ═══════════════════════════════ */
        .bm-celebrate .bm-body-group { animation: bm-celebrate-jump 1.2s ease-out; }
        @keyframes bm-celebrate-jump {
          0% { transform: translateY(0) scale(1) rotate(0); } 12% { transform: translateY(-20px) scale(1.12); }
          24% { transform: translateY(-16px) rotate(8deg); } 36% { transform: translateY(-10px) rotate(-8deg); }
          48% { transform: translateY(-6px) rotate(5deg); } 60% { transform: translateY(5px) scale(0.94); }
          75% { transform: translateY(-3px) scale(1.02); } 100% { transform: translateY(0) scale(1) rotate(0); }
        }
        .bm-celebrate .bm-antennae { animation: bm-celebrate-wave 1.4s ease-in-out; }
        @keyframes bm-celebrate-wave {
          0% { transform: rotate(0); } 12% { transform: rotate(18deg); } 30% { transform: rotate(-18deg); }
          48% { transform: rotate(12deg); } 65% { transform: rotate(-6deg); } 100% { transform: rotate(0); }
        }
        .bm-celebrate .bm-sparkle-1 { animation: bm-sparkle-burst 1.2s ease-out 0.1s forwards; }
        .bm-celebrate .bm-sparkle-2 { animation: bm-sparkle-burst 1.1s ease-out 0.2s forwards; }
        .bm-celebrate .bm-sparkle-3 { animation: bm-sparkle-burst 1s ease-out 0.35s forwards; }
        .bm-celebrate .bm-sparkle-4 { animation: bm-sparkle-burst 1.1s ease-out 0.25s forwards; }
        .bm-celebrate .bm-cheek-l, .bm-celebrate .bm-cheek-r { animation: bm-blush 0.3s ease-out forwards; }
        .bm-celebrate .bm-lid-l, .bm-celebrate .bm-lid-r { animation: bm-laugh-squint 1.5s ease-in-out; }
        .bm-celebrate .bm-shadow { animation: bm-shadow-bounce 1.2s ease-out; }

        /* ═══ WORRIED (error) ═══════════════════════════════════ */
        .bm-worried .bm-body-group { animation: bm-worried-shake 0.8s ease-in-out; }
        @keyframes bm-worried-shake {
          0%, 100% { transform: translateX(0) rotate(0); }
          12% { transform: translateX(-6px) rotate(-4deg); } 25% { transform: translateX(6px) rotate(4deg); }
          37% { transform: translateX(-5px) rotate(-3deg); } 50% { transform: translateX(5px) rotate(3deg); }
          62% { transform: translateX(-3px) rotate(-1deg); } 75% { transform: translateX(2px) rotate(1deg); }
        }
        .bm-worried .bm-eye-l, .bm-worried .bm-eye-r { animation: bm-eyes-widen 0.2s ease-out forwards; }
        .bm-worried .bm-pupil-group-l { animation: bm-worried-pupil-l 1.5s ease-in-out; }
        .bm-worried .bm-pupil-group-r { animation: bm-worried-pupil-r 1.5s ease-in-out; }
        @keyframes bm-worried-pupil-l { 0% { transform: translate(0,0) scale(1); } 15% { transform: translate(-2px,-2px) scale(0.7); } 100% { transform: translate(0,0) scale(1); } }
        @keyframes bm-worried-pupil-r { 0% { transform: translate(0,0) scale(1); } 15% { transform: translate(2px,-2px) scale(0.7); } 100% { transform: translate(0,0) scale(1); } }
        .bm-worried .bm-mouth { animation: bm-mouth-tremble 0.25s ease-in-out 4 alternate; }
        @keyframes bm-mouth-tremble { 0% { transform: scaleX(0.6) translateY(3px); } 100% { transform: scaleX(0.7) translateY(4px); } }
        .bm-worried .bm-antennae { animation: bm-worried-droop 0.4s ease-out forwards; }
        @keyframes bm-worried-droop { 0% { transform: translateY(0) rotate(0); } 100% { transform: translateY(6px) rotate(5deg) scaleY(0.85); } }
        .bm-worried .bm-sweat { animation: bm-sweat-drop 1.2s ease-in 0.2s forwards; }
        @keyframes bm-sweat-drop {
          0% { opacity: 0; transform: translateY(0) scale(0.5); } 10% { opacity: 0.8; transform: translateY(0) scale(1); }
          80% { opacity: 0.5; transform: translateY(20px) scale(0.8); } 100% { opacity: 0; transform: translateY(30px) scale(0.5); }
        }

        /* ═══ RUNNING — legs pump, body bobs fast ═══════════════ */
        .bm-running .bm-body-group { animation: bm-run-bob 0.35s ease-in-out infinite; }
        @keyframes bm-run-bob {
          0%, 100% { transform: translateY(0) translateX(0) rotate(0); }
          25%      { transform: translateY(-6px) translateX(2px) rotate(3deg); }
          50%      { transform: translateY(0) translateX(0) rotate(0); }
          75%      { transform: translateY(-6px) translateX(-2px) rotate(-3deg); }
        }
        .bm-running .bm-leg-l1 { animation: bm-run-leg-l 0.35s ease-in-out infinite; }
        .bm-running .bm-leg-r1 { animation: bm-run-leg-r 0.35s ease-in-out infinite; }
        .bm-running .bm-leg-l2 { animation: bm-run-leg-r 0.35s ease-in-out infinite; }
        .bm-running .bm-leg-r2 { animation: bm-run-leg-l 0.35s ease-in-out infinite; }
        @keyframes bm-run-leg-l {
          0%, 100% { transform: rotate(0deg) translateX(0); }
          25%      { transform: rotate(-30deg) translateX(-6px) translateY(-3px); }
          50%      { transform: rotate(0deg) translateX(0); }
          75%      { transform: rotate(20deg) translateX(4px) translateY(2px); }
        }
        @keyframes bm-run-leg-r {
          0%, 100% { transform: rotate(0deg) translateX(0); }
          25%      { transform: rotate(20deg) translateX(4px) translateY(2px); }
          50%      { transform: rotate(0deg) translateX(0); }
          75%      { transform: rotate(-30deg) translateX(-6px) translateY(-3px); }
        }
        .bm-running .bm-antennae { animation: bm-run-ant 0.35s ease-in-out infinite; }
        @keyframes bm-run-ant {
          0%, 100% { transform: rotate(-8deg); }
          50%      { transform: rotate(-15deg) translateY(2px); }
        }
        .bm-running .bm-shadow { animation: bm-run-shadow 0.35s ease-in-out infinite; }
        @keyframes bm-run-shadow {
          0%, 50%, 100% { transform: scaleX(1); opacity: 0.22; }
          25%, 75%      { transform: scaleX(0.7); opacity: 0.12; }
        }

        /* ═══ KICKING — ball appears, wind-up, kick, ball flies ═ */
        .bm-kicking .bm-body-group { animation: bm-kick-body 2.4s ease-in-out forwards; }
        @keyframes bm-kick-body {
          0%   { transform: translateX(0) rotate(0); }
          20%  { transform: translateX(-6px) rotate(-5deg); }
          35%  { transform: translateX(-8px) rotate(-10deg); }
          45%  { transform: translateX(8px) rotate(8deg); }
          55%  { transform: translateX(4px) rotate(4deg); }
          70%  { transform: translateX(0) rotate(0); }
        }
        .bm-kicking .bm-leg-r1 { animation: bm-kick-leg 2.4s ease-in-out forwards; }
        @keyframes bm-kick-leg {
          0%   { transform: rotate(0deg) translateX(0); }
          20%  { transform: rotate(-15deg) translateX(-4px); }
          35%  { transform: rotate(-25deg) translateX(-8px) translateY(-4px); }
          45%  { transform: rotate(40deg) translateX(12px) translateY(2px); }
          55%  { transform: rotate(25deg) translateX(8px); }
          70%  { transform: rotate(0deg) translateX(0); }
        }
        .bm-kicking .bm-ball { animation: bm-ball-fly 2.4s ease-in-out forwards; }
        @keyframes bm-ball-fly {
          0%   { opacity: 0; transform: translate(0, 0) scale(0.5); }
          15%  { opacity: 1; transform: translate(0, 0) scale(1); }
          35%  { opacity: 1; transform: translate(0, 0) scale(1); }
          50%  { opacity: 1; transform: translate(50px, -40px) scale(0.8) rotate(180deg); }
          65%  { opacity: 0.5; transform: translate(70px, -20px) scale(0.5) rotate(360deg); }
          80%  { opacity: 0; transform: translate(80px, 10px) scale(0.3) rotate(540deg); }
        }
        .bm-kicking .bm-antennae { animation: bm-kick-ant 2.4s ease-in-out forwards; }
        @keyframes bm-kick-ant {
          0% { transform: rotate(0); } 35% { transform: rotate(-5deg); } 45% { transform: rotate(10deg); } 60% { transform: rotate(0); }
        }

        /* ═══ SHARED KEYFRAMES ═══════════════════════════════════ */
        @keyframes bm-blush { 0% { opacity: 0.25; } 100% { opacity: 0.7; } }
        @keyframes bm-hide { 0% { opacity: 1; } 100% { opacity: 0; } }
        @keyframes bm-sparkle-burst {
          0% { opacity: 0; transform: scale(0) rotate(0deg); } 20% { opacity: 1; transform: scale(1.5) rotate(60deg); }
          45% { opacity: 0.8; transform: scale(1.1) rotate(120deg); } 100% { opacity: 0; transform: scale(0.3) rotate(200deg); }
        }

        /* ═══ REDUCED MOTION ═════════════════════════════════════ */
        @media (prefers-reduced-motion: reduce) {
          .bug-mascot, .bug-mascot * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </>
  )
}
